import styled from 'styled-components';

import { DesignAvatar } from '@components/design-avatar';
import { EmptyStateCell } from '@components/empty-state';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAiAgent } from '@components/icons/ai-agent';
import { Spinner } from '@components/loading-spinner/loading-spinner';
import { hideModal } from '@components/modal';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { AIAgent } from 'api/ai-agents/requests';
import { useAgentDetailsSidebar } from 'ui/authenticated/main/pages/ai-agents/agent-details-sidebar';

import {
  ActionItem,
  OnCreateActionSelectProps,
  SearchEmptyState,
  TabContentWrapper,
} from './common';

type AIAgentsTabProps = {
  aiAgents: AIAgent[] | undefined;
  isLoading: boolean;
  onSelect: (props: OnCreateActionSelectProps) => void;
  searchQuery?: string;
  onClearSearch: () => void;
};

export const AIAgentsTab = ({
  aiAgents,
  isLoading,
  onSelect,
  searchQuery,
  onClearSearch,
}: AIAgentsTabProps) => {
  const { t } = useScopedTranslate(locale);
  const showAgentDetailsSidebar = useAgentDetailsSidebar();

  const handleAddAgent = () => {
    hideModal();
    showAgentDetailsSidebar();
  };

  const hasNoSearchResults = searchQuery && aiAgents?.length === 0;

  return (
    <TabContentWrapper>
      {isLoading ? (
        <Spinner />
      ) : hasNoSearchResults ? (
        <SearchEmptyState onClearSearch={onClearSearch} />
      ) : (
        <>
          {aiAgents && aiAgents.length === 0 && (
            <div style={{ marginBottom: pxToRem(16) }}>
              <EmptyStateCell icon={<IconAiAgent />} text={t('noAiAgents')} />
            </div>
          )}
          <AgentWrapper>
            <ActionItem
              text={t('addAgent')}
              description={t('addAgentDescription')}
              icon={<IconAddPlus color="aiGradientTeal100" />}
              type="ask_ai_agent"
              onSelect={handleAddAgent}
            />
            {aiAgents?.map((agent) => (
              <ActionItem
                key={agent.shortId}
                text={agent.name}
                description={agent.description}
                icon={<DesignAvatar size={24} src={agent.picture} />}
                type="ask_ai_agent"
                onSelect={() =>
                  onSelect({
                    actionType: 'ask_ai_agent',
                    values: { aiAgentId: agent.shortId },
                  })
                }
              />
            ))}
          </AgentWrapper>
        </>
      )}
    </TabContentWrapper>
  );
};

const AgentWrapper = styled.div({
  '.action-item': {
    padding: pxToRem(8),
  },
});

const locale = {
  en: {
    addAgent: 'Add agent',
    addAgentDescription: 'Create a new agent',
    noAiAgents: 'No AI agents configured',
  },
};
